/**
 * Check if the image is acceptable to be blurred by Next.js Image component
 *
 * Next.js's "blur" placeholder works by creating a very small and blurry version of the image (typically 10x10 pixels) that is used as a placeholder while the actual image loads. This feature is particularly useful for large images because it:
 * - Improves user experience by immediately showing something
 * - Prevents layout shifts during loading
 * - Creates a smooth transition between loading state and final image
 *
 * However, for very small images (less than 40x40 pixels), this feature becomes problematic because:
 * - The blurred version would be even smaller (about 2-3 pixels), making it practically useless
 * - The cost of generating the placeholder could be higher than the benefit gained
 * - The quality of the blurred image would be so poor that it could be more distracting than helpful
 *
 * @param w - width of the image
 * @param h - height of the image
 * @returns true if the image is acceptable to be blurred, false otherwise
 */
export const isAcceptableSizeToBeBlurredByNextImage = (w: number, h: number) => {
  return w >= 40 && h >= 40;
};

/**
 * Check if the image exists
 * @param url - url of the image
 * @returns true if the image exists, false otherwise
 */
export const doesImageExist = (url: string): Promise<boolean> => {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};
